import { useCallback } from 'react';
import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";

import { UserDTO } from '../../../apiclients/bstinsights';
import { default as useAcquireToken } from "../../AuthToken/Hooks/useAcquireToken";
import { default as useBSTInsightsAPI } from "../../../apiclients/api/bstinsightsapi";
import { useGetUsersPagedQueryKey } from './useGetUsersPaged';
import { useGridDataQueryInvalidate } from "./useGridDataQueryInvalidate"

type Payload = {
  user: UserDTO;
  roleId: number;
}

type OptimisticUpdatePayload = {
  user: UserDTO;
  roleId: number;
  isChecked: boolean
}

/* istanbul ignore next */
const useOptimistUpdate = () => {
  const queryClient = useQueryClient()
  const gridQueryKey = useGetUsersPagedQueryKey()

  return useCallback(async ({ user, roleId, isChecked }: OptimisticUpdatePayload) => {
    let groups = [...(user.groups ?? [])]
    const userId = Number(user.userId)
    if (isChecked) {
      groups.push(roleId)
    } else {
      groups = groups.filter((id) => id !== roleId)
    }

    await queryClient.cancelQueries({ queryKey: gridQueryKey })

    const previousData = queryClient.getQueryData(gridQueryKey)

    // Update the cache optimistically
    queryClient.setQueryData(gridQueryKey, (previousData: InfiniteData<UserDTO[], unknown> | undefined) => {
      if (previousData) {
        return {
          ...previousData,
          pages: previousData.pages.map((page) => {
            const user = page.find((u) => Number(u.userId) === userId);

            if (user) {
              user.groups = groups
            }

            return page;
          }),
        };
      }
    });
    return { previousData }
  }, [gridQueryKey, queryClient])
}

export const useUpdateUserRole = () => {
  const {
    addUserRole: addUserRoleAPI,
    removeUserRole: removeUserRoleAPI
  } = useBSTInsightsAPI();
  const { getTokenSilently } = useAcquireToken();
  const invalidateGridQuery = useGridDataQueryInvalidate();
  const optimisticallyUpdateUser = useOptimistUpdate()

  const { mutate: addUserRole } = useMutation({
    mutationFn: async (data: Payload) => {
      const token = await getTokenSilently();
      if (!token) return;
      await addUserRoleAPI(
        token,
        {
          userId: Number(data.user.userId),
          roleId: data.roleId
        }
      );
    },
    onMutate: (data) => optimisticallyUpdateUser({ ...data, isChecked: true }),
    onSettled: invalidateGridQuery
  });

  const { mutate: removeUserRole } = useMutation({
    mutationFn: async (data: Payload) => {
      const token = await getTokenSilently();
      if (!token) return;
      await removeUserRoleAPI(
        token,
        {
          userId: Number(data.user.userId),
          roleId: data.roleId
        }
      );
    },
    onMutate: (data) => optimisticallyUpdateUser({ ...data, isChecked: false }),
    onSettled: invalidateGridQuery
  });

  return {
    addUserRole,
    removeUserRole,
  };
};
